import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarIcon, AlertTriangleIcon, CreditCardIcon, Clock, Users, CheckCircle2, AlertCircle, XCircle, Edit, Eye, Mail, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { useState } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";

interface License {
  id: number;
  applicationName: string;
  name: string;
  cost: number;
  billingCycle: string;
  renewalDate: string;
  status: string;
  assignedUsers?: number;
  assignedSeats?: number;
  totalSeats?: number;
  lastPaymentMethod?: string;
  autoRenewal?: boolean;
  category?: string;
}

export default function UpcomingRenewalsTable() {
  console.log("🚀 UpcomingRenewalsTable component loaded - RENEWAL FIX ACTIVE");
  
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [timeFilter, setTimeFilter] = useState<string>("7");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [renewingLicenseId, setRenewingLicenseId] = useState<number | null>(null);
  
  const { data, isLoading } = useQuery<License[]>({
    queryKey: ['/api/renewals'],
    placeholderData: [],
  });

  const renewals = data || [];

  // Build month options dynamically from the data for the quick-month filter
  const monthOptions = Array.from(
    new Set(renewals.map(r => {
      const d = new Date(r.renewalDate);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }))
  ).sort();

  // Compute the effective status from the renewal date rather than the raw DB value.
  // Must be declared before filteredRenewals below -- it's called from inside that
  // filter whenever statusFilter isn't "all", and being a const, it has no hoisting:
  // declaring it after its first use threw "Cannot access before initialization"
  // (minified to a single letter in production) the moment that branch ran.
  const getEffectiveStatus = (renewal: License): string => {
    const isLifetime = renewal.billingCycle === 'Lifetime' || renewal.billingCycle === 'One-Time';
    if (isLifetime) return renewal.status ?? 'active';
    const days = Math.ceil(
      (new Date(renewal.renewalDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (days < 0) return 'expired';
    if (days <= 30) return 'expiring';
    return renewal.status ?? 'active';
  };

  const filteredRenewals = renewals
    .filter(renewal => {
      const renewalDate = new Date(renewal.renewalDate);
      const today = new Date();
      const daysUntilRenewal = Math.ceil((renewalDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      // Time-window filter (skip for "all upcoming")
      const timeFilterNum = parseInt(timeFilter);
      if (!isNaN(timeFilterNum) && daysUntilRenewal > timeFilterNum) return false;

      // Status filter — compare against effective status, not raw DB value
      if (statusFilter !== "all" && getEffectiveStatus(renewal) !== statusFilter) return false;

      // Month filter
      if (monthFilter !== "all") {
        const ym = `${renewalDate.getFullYear()}-${String(renewalDate.getMonth() + 1).padStart(2, '0')}`;
        if (ym !== monthFilter) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const diff = new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime();
      return sortDir === "asc" ? diff : -diff;
    });

  // Simple date formatting helper
  const formatDueIn = (renewalDate: string): string => {
    const now = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = renewal.getTime() - now.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `Due in ${days} days`;
  };

  const getBadgeVariant = (renewalDate: string) => {
    const now = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = renewal.getTime() - now.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (days < 0) return "destructive";
    if (days <= 7) return "destructive";
    if (days <= 30) return "secondary";
    return "outline";
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'expiring':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getUtilizationColor = (assigned: number, total: number) => {
    const utilization = (assigned / total) * 100;
    if (utilization >= 90) return "text-red-600";
    if (utilization >= 70) return "text-yellow-600";
    return "text-green-600";
  };

  const handleManageClick = (license: License) => {
    // Navigate to license management page
    navigate(`/licenses/${license.id}`);
  };

  const handleDetailsClick = (license: License) => {
    setSelectedLicense(license);
    setDetailsDialogOpen(true);
  };

  const handleRenewNow = async (licenseId: number) => {
    console.log("🔄 handleRenewNow called for license ID:", licenseId);
    
    if (renewingLicenseId) {
      console.log("⚠️  Already renewing another license, returning");
      return;
    }
    
    setRenewingLicenseId(licenseId);
    
    try {
      console.log("📤 Sending POST to /api/renewals/mark-renewed with:", { licenseIds: [licenseId] });
      
      const response = await fetch("/api/renewals/mark-renewed", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ licenseIds: [licenseId] }),
      });
      
      console.log("📥 Response status:", response.status, response.statusText);
      
      const result = await response.json();
      console.log("📥 Response body:", result);
      
      if (response.ok) {
        toast({
          title: "License Renewed Successfully",
          description: `License has been renewed and historical record created.`,
        });
        
        // Close the details dialog if it's open
        setDetailsDialogOpen(false);
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
        queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      } else {
        toast({
          title: "Renewal Failed",
          description: result.message || "Failed to renew license. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("❌ Error renewing license:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while renewing the license.",
        variant: "destructive",
      });
    } finally {
      setRenewingLicenseId(null);
    }
  };

  return (
    <Card className="col-span-12">
      <CardHeader className="border-b border-slate-200 py-4 px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-slate-500" />
            <CardTitle className="text-lg font-semibold">Upcoming Renewals</CardTitle>
            <Badge variant="outline" className="ml-2">
              {filteredRenewals.length} items
            </Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={timeFilter} onValueChange={v => { setTimeFilter(v); setMonthFilter("all"); }}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Next 7 days</SelectItem>
                <SelectItem value="30">Next 30 days</SelectItem>
                <SelectItem value="90">Next 90 days</SelectItem>
                <SelectItem value="365">All upcoming</SelectItem>
              </SelectContent>
            </Select>
            <Select value={monthFilter} onValueChange={v => { setMonthFilter(v); if (v !== "all") setTimeFilter("365"); }}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="By month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {monthOptions.map(ym => {
                  const [yr, mo] = ym.split('-');
                  const label = new Date(parseInt(yr), parseInt(mo) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                  return <SelectItem key={ym} value={ym}>{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expiring">Expiring</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filteredRenewals.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No renewals found</h3>
            <p className="text-slate-500">No licenses match your current filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-slate-200">
                <TableHead className="px-4 py-3 text-left font-medium text-slate-700">License & Application</TableHead>
                <TableHead className="px-4 py-3 text-left font-medium text-slate-700">Usage</TableHead>
                <TableHead className="px-4 py-3 text-left font-medium text-slate-700">Cost & Billing</TableHead>
                <TableHead className="px-4 py-3 text-left font-medium text-slate-700">
                  <button
                    className="flex items-center gap-1 hover:text-slate-900 transition-colors group"
                    onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
                  >
                    Renewal Date
                    {sortDir === "asc"
                      ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
                      : <ArrowDown className="h-3.5 w-3.5 text-primary" />}
                  </button>
                </TableHead>
                <TableHead className="px-4 py-3 text-left font-medium text-slate-700">Status</TableHead>
                <TableHead className="px-4 py-3 text-center font-medium text-slate-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRenewals.map((renewal, index) => {
                // Use authentic database values for seat utilization
                const assignedUsers = renewal.assignedUsers ?? renewal.assignedSeats ?? 0;
                const totalSeats = renewal.totalSeats || 1;
                const utilization = Math.round((assignedUsers / totalSeats) * 100);
                
                return (
                  <TableRow key={renewal.id || index} className="hover:bg-slate-50 border-b border-slate-100">
                    <TableCell className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mr-3 text-white text-sm font-medium shadow-sm">
                          {renewal.applicationName?.charAt(0)?.toUpperCase() || 'L'}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-slate-900">{renewal.applicationName}</div>
                          <div className="text-xs text-slate-500">{renewal.name}</div>
                          {renewal.category && (
                            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded mt-1 inline-block">
                              {renewal.category}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-slate-400" />
                        <div>
                          <div className={`text-sm font-medium ${getUtilizationColor(assignedUsers, totalSeats)}`}>
                            {assignedUsers}/{totalSeats} seats
                          </div>
                          <div className="text-xs text-slate-500">
                            {utilization}% utilized
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-4">
                      <div>
                        <div className="font-medium text-sm text-slate-900">
                          {formatCurrency(renewal.cost)}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {renewal.billingCycle}
                        </div>
                        {renewal.lastPaymentMethod && (
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <CreditCardIcon className="h-3 w-3" />
                            {renewal.lastPaymentMethod}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-4">
                      <div>
                        <div className="font-medium text-sm text-slate-900">
                          {new Date(renewal.renewalDate).toLocaleDateString()}
                        </div>
                        <Badge variant={getBadgeVariant(renewal.renewalDate)} className="text-xs mt-1">
                          {formatDueIn(renewal.renewalDate)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(getEffectiveStatus(renewal))}
                        <span className="text-sm capitalize">{getEffectiveStatus(renewal)}</span>
                        {renewal.autoRenewal && (
                          <Badge variant="outline" className="text-xs ml-2">
                            Auto-renew
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="h-8 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log("🔴 BUTTON CLICKED! License ID:", renewal.id);
                            handleRenewNow(renewal.id);
                          }}
                          disabled={renewingLicenseId === renewal.id}
                          data-testid={`button-renew-${renewal.id}`}
                        >
                          <RefreshCw className={`h-3 w-3 mr-1 ${renewingLicenseId === renewal.id ? 'animate-spin' : ''}`} />
                          {renewingLicenseId === renewal.id ? 'Renewing...' : 'Renew'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 px-3 text-xs"
                          onClick={() => handleManageClick(renewal)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Manage
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* License Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                {selectedLicense?.applicationName?.charAt(0)?.toUpperCase()}
              </div>
              License Details - {selectedLicense?.applicationName}
            </DialogTitle>
            <DialogDescription>
              Comprehensive information about this software license
            </DialogDescription>
          </DialogHeader>
          
          {selectedLicense && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-2">License Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Application:</span>
                        <span className="font-medium">{selectedLicense.applicationName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">License Name:</span>
                        <span className="font-medium">{selectedLicense.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Status:</span>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(selectedLicense.status)}
                          <span className="capitalize">{selectedLicense.status}</span>
                        </div>
                      </div>
                      {selectedLicense.category && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Category:</span>
                          <Badge variant="outline" className="text-xs">
                            {selectedLicense.category}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Billing & Costs</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Cost:</span>
                        <span className="font-medium">{formatCurrency(selectedLicense.cost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Billing Cycle:</span>
                        <span className="font-medium">{selectedLicense.billingCycle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Renewal Date:</span>
                        <span className="font-medium">
                          {new Date(selectedLicense.renewalDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Auto-renewal:</span>
                        <Badge variant={selectedLicense.autoRenewal ? "default" : "outline"} className="text-xs">
                          {selectedLicense.autoRenewal ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Statistics */}
              <div>
                <h4 className="text-sm font-medium text-slate-900 mb-2">Usage Statistics</h4>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {selectedLicense.assignedUsers ?? selectedLicense.assignedSeats ?? 0}
                      </div>
                      <div className="text-xs text-slate-500">Assigned Users</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {selectedLicense.totalSeats || 1}
                      </div>
                      <div className="text-xs text-slate-500">Total Seats</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {Math.round((((selectedLicense.assignedUsers ?? selectedLicense.assignedSeats ?? 0) / (selectedLicense.totalSeats || 1)) * 100))}%
                      </div>
                      <div className="text-xs text-slate-500">Utilization</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t border-slate-200">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setDetailsDialogOpen(false);
                      handleManageClick(selectedLicense);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit License
                  </Button>
                  <Button variant="outline" size="sm">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Reminder
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="default" 
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => selectedLicense && handleRenewNow(selectedLicense.id)}
                    disabled={!selectedLicense || renewingLicenseId === selectedLicense?.id}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${renewingLicenseId === selectedLicense?.id ? 'animate-spin' : ''}`} />
                    {renewingLicenseId === selectedLicense?.id ? 'Renewing...' : 'Renew Now'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setDetailsDialogOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
